import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { finalize, Observable, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';

import { ViQuestionApi } from '../../../api/visibilityIntelligence/models/vi-question.api.model';
import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { buildDimensionCounts } from '../../../api/visibilityIntelligence/models/vi-generate-questions.api.model';

import { ViMonitor } from '../../models/vi-monitor.model';
import { ViMonitorService } from '../../services/vi-monitor.service';

interface DimensionCountOption {
    key: ViDimensionEnum;
    label: string;
    count: number;
    existingCount: number;
}

@Component({
    selector: 'agpr-vi-generate-additional-questions-panel',
    templateUrl: './vi-generate-additional-questions-panel.component.html',
    styleUrls: ['./vi-generate-additional-questions-panel.component.scss'],
    standalone: false
})
export class ViGenerateAdditionalQuestionsPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @Input() monitor: ViMonitor;
    @Input() questions: ViQuestionApi[] = [];

    readonly id = 'viGenerateAdditionalQuestionsPanel';
    readonly buttonTypes = ButtonTypesEnum;
    readonly questionCountLimit: number = 25;

    hideSlideOverlay: boolean = false;
    subscriptions: Subscription[] = [];

    dimensions: DimensionCountOption[] = [];
    canGenerate: boolean = false;
    isGenerating: boolean = false;
    generatedQuestions: ViQuestionApi[] | null = null;
    createdQuestionsCount: number = 0;

    constructor(
        public coreDialogService: CoreDialogService,
        private viMonitorService: ViMonitorService
    ) {
        super(coreDialogService, 'ViGenerateAdditionalQuestionsPanel');
    }

    ngOnInit(): void {
        this.dimensions = Object.values(ViDimensionEnum).map(dimension => ({
            key: dimension,
            label: `VisibilityIntelligence.querySetup.dimensions.${dimension}`,
            count: 0,
            existingCount: this.questions.filter(question => question.dimension === dimension).length
        }));
        this.createdQuestionsCount = this.questions.length;
    }

    onQuestionCountChange(): void {
        this.canGenerate = this.dimensions.some(dimension => dimension.count > 0);
        this.createdQuestionsCount = this.questions.length + this.dimensions.reduce((sum, dimension) => sum + dimension.count, 0);
    }

    generate(): void {
        if (!this.canGenerate || this.isGenerating || !this.monitor) {
            return;
        }

        this.isGenerating = true;
        this.viMonitorService.generateQuestions(this.monitor, this.questions, buildDimensionCounts(this.dimensions)).pipe(
            finalize(() => this.isGenerating = false)
        ).subscribe((response) => {
            this.generatedQuestions = response.questions;
            this.hideSlideOverlay = true;
        });
    }

    cancel(): void {
        this.hideSlideOverlay = true;
    }

    close(overlayVisibility: boolean): void {
        if (!overlayVisibility) {
            this.closeDialog(this.generatedQuestions);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
